/**
 * What a signed-in user sees when the shell could not learn whether they must verify their
 * agency address: the API turned their token away, or the answer could not be read.
 *
 * It sits in the sign-in gate's chrome and offers one way forward. The portal behind the
 * gate stays unmounted either way.
 */
import { Button, Card, mergeClasses, Title2 } from '@fluentui/react-components';
import { ErrorCircleRegular } from '@fluentui/react-icons';
import { LoginShell } from '../LoginShell';
import { useLoginStyles } from '../loginStyles';

export interface GateErrorPageProps {
  /** The portal-owned sentence describing the failure. */
  message: string;
  /** The single action offered, e.g. "Try again" or "Sign in again". */
  actionLabel: string;
  onAction: () => void;
}

export function GateErrorPage({ message, actionLabel, onAction }: GateErrorPageProps) {
  const loginStyles = useLoginStyles();

  return (
    <LoginShell>
      <Card
        appearance="outline"
        className={mergeClasses(loginStyles.card, loginStyles.cardNarrow)}
        role="alert"
      >
        <div className={loginStyles.centeredHeading}>
          <span
            className={mergeClasses(loginStyles.headingIcon, loginStyles.centeredHeadingIcon)}
            aria-hidden="true"
          >
            <ErrorCircleRegular />
          </span>
          <Title2 as="h1" id="login-content-title" tabIndex={-1}>
            We couldn&apos;t check your account
          </Title2>
          <p>{message}</p>
        </div>
        <div className={loginStyles.actions}>
          <Button appearance="primary" onClick={onAction}>
            {actionLabel}
          </Button>
        </div>
      </Card>
    </LoginShell>
  );
}
